import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import React, { type ReactNode, useCallback } from "react";
import { FlatList, type ListRenderItemInfo, View } from "react-native";
import { api } from "../utils/convex-api";
import type {
  BookingItem,
  SessionItem,
} from "@teachloop/backend/api/app/parent/schedule/queries";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import ParentSessionCard from "../shared/parent-session-card";
import ParentBookingCard from "../shared/parent-booking-card";
import ScheduleEmptyState from "./schedule-empty-state";

function ParentScheduleList({
  scheduleList,
  header,
  statusFilter,
}: {
  scheduleList: SessionItem[] | BookingItem[];
  header: ReactNode;
  statusFilter?: "upcoming" | "requests" | "completed";
}) {
  const responsiveWidth = useResponsiveWidth();
  const { mutateAsync: cancelBooking, isPending: isCancellingBookingRequest } =
    useMutation({
      mutationFn: useConvexMutation(api.parent.home.mutations.cancelBooking),
    });

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<SessionItem | BookingItem>) => {
      const renderCard = () => {
        switch (item.type) {
          case "session":
            return (
              <ParentSessionCard
                key={item.id}
                session={{
                  _id: item.id,
                  subject: item.subject,
                  type: item.sessionType,
                  deliveryMethod: item.deliveryMethod,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  tutor: {
                    ...item.tutor,
                    firstName: item.tutor.name?.split(" ")?.[0],
                    lastName: item.tutor.name?.split(" ")?.[1],
                  },
                  student: {
                    id: item.student.id,
                  },
                  status: item.status,
                  tutorNotes: item.tutorNotes,
                  parentNotes: item.parentNotes,
                  url: item.url,
                }}
              />
            );
          case "booking":
            return (
              <ParentBookingCard
                key={item.id}
                isRecurring={false}
                bookings={[
                  {
                    _id: item.id,
                    subject: item.subject,
                    type: item.deliveryMethod,
                    startTime: item.startTime,
                    endTime: item.endTime,
                    tutor: {
                      ...item.tutor,
                      firstName: item.tutor.name.split(" ")[0],
                      lastName: item.tutor.name.split(" ")[1],
                    },
                    recurringGroupId: null,
                  },
                ]}
                onCancelRequest={() => cancelBooking({ bookingId: item.id })}
                isCancellingBookingRequest={isCancellingBookingRequest}
              />
            );

          default:
            return null;
        }
      };
      return <View className="mx-5">{renderCard()}</View>;
    },
    [cancelBooking, isCancellingBookingRequest]
  );

  const getEmptyStateMessage = () => {
    switch (statusFilter) {
      case "upcoming":
        return "Your child has no upcoming lessons scheduled.";
      case "requests":
        return "Your child has no pending booking requests.";
      case "completed":
        return "Your child has no completed lessons yet.";
      default:
        return "Your child has no lessons scheduled.";
    }
  };

  const ListEmptyComponent = useCallback(() => {
    return <ScheduleEmptyState message={getEmptyStateMessage()} />;
  }, [statusFilter]);

  return (
    <FlatList
      data={scheduleList}
      renderItem={renderItem}
      ListHeaderComponent={() => header}
      ListEmptyComponent={ListEmptyComponent}
      ItemSeparatorComponent={() => <View className="h-4" />}
      ListFooterComponent={() => <View className="h-4" />}
      style={{ width: responsiveWidth, alignSelf: "center" }}
    />
  );
}

export default ParentScheduleList;
